/**
 * The registrar's extra-time book for timed native quizzes (S4, migration 229).
 * Only the registrar reads these routes: approver, reference and reasons never
 * reach a teaching seat or the learner.
 */
import { api } from "@/lib/api/client";
import { commitOptions } from "@/lib/api/commitSeams";

export type AllowanceAction = "recorded" | "corrected" | "revoked";

export interface AllowanceCourse {
  id: string;
  code: string | null;
  title: string;
}

/** One revision as the book keeps it; the record is its newest revision. */
export interface AllowanceRevision {
  id: string;
  student_id: string;
  revision: number;
  action: AllowanceAction;
  percent: number;
  effective_from: string;
  effective_until: string | null;
  all_quizzes: boolean;
  courses: AllowanceCourse[];
  approver: string;
  reference: string;
  reason: string;
  created_at: string;
  actor_name: string | null;
  learner_name: string | null;
  sid: string;
}

export interface Allowance extends AllowanceRevision {
  /** Every revision, newest first. */
  history: AllowanceRevision[];
}

export interface AllowanceTerms {
  percent: number;
  effective_from: string;
  effective_until: string | null;
  all_quizzes: boolean;
  course_ids: string[];
  approver: string;
  reference: string;
}

export type SaveAllowance =
  | ({ expected_revision: 0; action: "recorded" } & AllowanceTerms)
  | ({ expected_revision: number; action: "corrected"; reason: string } & AllowanceTerms)
  | { expected_revision: number; action: "revoked"; reason: string };

export interface SavedAllowance {
  state: "confirmed";
  id: string;
  revision: number;
  action: AllowanceAction;
}

export const timeAllowancesKey = (studentId: string) => ["time-allowances", studentId] as const;
export const TIME_ALLOWANCES_KEY = ["time-allowances"] as const;

export const timeAllowancesApi = {
  list: () => api.get<{ allowances: AllowanceRevision[] }>("/time-allowances"),
  ofLearner: (studentId: string) => api.get<{ allowances: Allowance[] }>(`/students/${studentId}/time-allowances`),
  save: (studentId: string, allowanceId: string, body: SaveAllowance, key: string) =>
    api.put<SavedAllowance>(`/students/${studentId}/time-allowances/${allowanceId}`, body, commitOptions(key)),
};
